import { api } from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import {
  VehicleCodeParams,
  VehicleCodeResponse,
  VehicleCode,
  SaleSlipVehicleCodeParams,
} from "@/interface/standard/st_vehicle_code";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export const useVehicleCode = (params: VehicleCodeParams) => {
  const { page = 0, size = 20, number1, sort, employmentStatus, exactMatch } = params;
  return useQuery<VehicleCodeResponse>({
    queryKey: ["vehicleCode", page, size, number1, sort, employmentStatus, exactMatch],
    queryFn: async () => {
      const response = await api.get("/vehicle-code", { params });
      return response.data;
    },
  });
};

export const useSaleSlipVehicleCode = (params: SaleSlipVehicleCodeParams) => {
  const { page = 0, size = 20, number1, sort, gb2 } = params;
  return useQuery<VehicleCodeResponse>({
    queryKey: ["saleSlipVehicleCode", page, size, number1, sort, gb2],
    queryFn: async () => {
      const response = await api.get("/vehicle-code/saleSlip", { params });
      return response.data;
    },
    staleTime: 1000 * 60, // 1분간 캐시 유지
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

export const useInsertVehicleCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<VehicleCode>) => {
      const response = await api.post("/vehicle-code", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicleCode"] });
      toast.success("차량 코드가 추가되었습니다.");
    },
  });
};

export const useUpdateVehicleCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ stVehicleCode, data }: { stVehicleCode: string; data: Partial<VehicleCode> }) => {
      const response = await api.put(`/vehicle-code/${stVehicleCode}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicleCode"] });
      toast.success("차량 코드가 수정되었습니다.");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "차량 코드 수정에 실패했습니다.");
    },
  });
};

export const useDeleteVehicleCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (stVehicleCode: string) => {
      const response = await api.delete(`/vehicle-code/${stVehicleCode}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicleCode"] });
      toast.success("차량 코드가 삭제되었습니다.");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "차량 코드 삭제에 실패했습니다.");
    },
  });
};
